import * as THREE from 'three';
import type { CharacterId } from '../content/characters';
import type { AnimatedCharacter, CharacterLibrary } from '../render/characters';

/**
 * A small, self-contained showroom renderer for the Timeline character picker.
 * It deliberately shares CharacterLibrary's already-loaded models and clips,
 * while owning its camera, lights and per-preview materials.
 */
export class CharacterPreview {
  private readonly renderer: THREE.WebGLRenderer;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(27, 1, 0.1, 30);
  private readonly clock = new THREE.Clock();
  private readonly stage = new THREE.Group();
  private avatar: AnimatedCharacter | null = null;
  private frame = 0;
  private everConnected = false;
  private entrance = 1;
  private targetYaw = -0.18;
  private yaw = -0.18;
  private dragging = false;
  private pointerX = 0;

  constructor(private readonly canvas: HTMLCanvasElement, private readonly library: CharacterLibrary) {
    this.renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true, powerPreference: 'high-performance' });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.22;

    this.camera.position.set(0, 1.06, 5.2);
    this.camera.lookAt(0, 0.93, 0);
    this.scene.add(this.stage);

    const hemi = new THREE.HemisphereLight(0xc9f7ff, 0x071018, 2.15);
    const key = new THREE.DirectionalLight(0xe7fbff, 4.2);
    key.position.set(-3.5, 5.2, 4.5);
    const rim = new THREE.DirectionalLight(0x43d9ff, 5.1);
    rim.position.set(4, 2.7, -3.2);
    const warm = new THREE.PointLight(0xffcf8a, 5.5, 8);
    warm.position.set(-2.5, 0.65, -1.8);
    this.scene.add(hemi, key, rim, warm);

    const platform = new THREE.Mesh(
      new THREE.CylinderGeometry(1.05, 1.22, 0.12, 64),
      new THREE.MeshStandardMaterial({ color: 0x0a1a23, metalness: 0.72, roughness: 0.25, emissive: 0x073744, emissiveIntensity: 0.5 }),
    );
    platform.position.y = -0.08;
    this.scene.add(platform);

    const ring = new THREE.Mesh(
      new THREE.TorusGeometry(1.08, 0.014, 8, 96),
      new THREE.MeshBasicMaterial({ color: 0x62e5ff, transparent: true, opacity: 0.72 }),
    );
    ring.rotation.x = Math.PI / 2;
    ring.position.y = -0.005;
    this.scene.add(ring);

    canvas.addEventListener('pointerdown', (event) => {
      this.dragging = true;
      this.pointerX = event.clientX;
      canvas.setPointerCapture(event.pointerId);
    });
    canvas.addEventListener('pointermove', (event) => {
      if (!this.dragging) return;
      this.targetYaw += (event.clientX - this.pointerX) * 0.012;
      this.pointerX = event.clientX;
    });
    const stopDrag = () => { this.dragging = false; };
    canvas.addEventListener('pointerup', stopDrag);
    canvas.addEventListener('pointercancel', stopDrag);

    this.frame = requestAnimationFrame(() => this.animate());
  }

  setCharacter(id: CharacterId): void {
    this.avatar?.dispose();
    this.avatar = this.library.create(id);
    if (!this.avatar) return;
    this.avatar.object.position.y = 0;
    this.stage.add(this.avatar.object);
    this.targetYaw = -0.18;
    this.yaw = -0.75;
    this.entrance = 0;
  }

  private animate(): void {
    if (this.canvas.isConnected) this.everConnected = true;
    if (this.everConnected && !this.canvas.isConnected) {
      this.dispose();
      return;
    }

    const dt = Math.min(this.clock.getDelta(), 0.05);
    this.entrance = Math.min(1, this.entrance + dt * 4.4);
    const eased = 1 - Math.pow(1 - this.entrance, 3);
    this.yaw += (this.targetYaw - this.yaw) * Math.min(1, dt * 8);
    this.stage.rotation.y = this.yaw;
    this.stage.position.y = (1 - eased) * -0.12;
    this.stage.scale.setScalar(0.9 + eased * 0.1);
    this.avatar?.update(dt, 'idle', 0);

    const width = Math.max(1, this.canvas.clientWidth);
    const height = Math.max(1, this.canvas.clientHeight);
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const targetWidth = Math.round(width * dpr);
    const targetHeight = Math.round(height * dpr);
    if (this.canvas.width !== targetWidth || this.canvas.height !== targetHeight) {
      this.renderer.setPixelRatio(dpr);
      this.renderer.setSize(width, height, false);
      this.camera.aspect = width / height;
      this.camera.updateProjectionMatrix();
    }
    this.renderer.render(this.scene, this.camera);
    this.frame = requestAnimationFrame(() => this.animate());
  }

  dispose(): void {
    cancelAnimationFrame(this.frame);
    this.avatar?.dispose();
    this.avatar = null;
    for (const object of this.scene.children) {
      const mesh = object as THREE.Mesh;
      if (mesh.geometry) mesh.geometry.dispose();
      if (mesh.material) {
        const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
        for (const material of materials) material.dispose();
      }
    }
    this.renderer.dispose();
  }
}
